import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  reportData: null,
  units: [],
  deviceStatuses: [],
  deviceSettingTypes: [],
  connectionStatuses: [],
  loading: false,
  exportLoading: false,
  pdfExportLoading: false,
  refreshLoading: false,
  error: null,
  successMessage: null,
  // API Filters (for fetching data)
  filterUnitId: '',
  filterFromDate: '',
  filterToDate: '',
  // Local Table Filters
  filterTableDeviceTypes: [],
  filterTableStatuses: [],
  filterTableConnectionStatuses: [],
  filterLastCommFromDate: '',
  filterLastCommToDate: '',
  // Sorting
  sortColumn: 'deviceAddress',
  sortDirection: 'asc',
};

const deviceReportSlice = createSlice({
  name: 'deviceReport',
  initialState,
  reducers: {
    // Fetch init data (units)
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.units = action.payload.units || [];
      state.deviceStatuses = action.payload.deviceStatuses || [];
      state.deviceSettingTypes = action.payload.deviceSettingTypes || [];
      state.connectionStatuses = action.payload.connectionStatuses || [];
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch device report
    fetchDeviceReport: (state) => {
      state.loading = true;
      state.error = null;
      state.reportData = null; // Clear old data before fetching new data
    },
    fetchDeviceReportSuccess: (state, action) => {
      state.loading = false;
      state.reportData = action.payload;
      state.error = null;
    },
    fetchDeviceReportFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // API Filters
    setFilterUnitId: (state, action) => {
      state.filterUnitId = action.payload;
    },
    setFilterFromDate: (state, action) => {
      state.filterFromDate = action.payload;
    },
    setFilterToDate: (state, action) => {
      state.filterToDate = action.payload;
    },

    // Local Table Filters
    setFilterTableDeviceTypes: (state, action) => {
      state.filterTableDeviceTypes = action.payload;
    },
    setFilterTableStatuses: (state, action) => {
      state.filterTableStatuses = action.payload;
    },
    setFilterTableConnectionStatuses: (state, action) => {
      state.filterTableConnectionStatuses = action.payload;
    },
    setFilterLastCommFromDate: (state, action) => {
      state.filterLastCommFromDate = action.payload;
    },
    setFilterLastCommToDate: (state, action) => {
      state.filterLastCommToDate = action.payload;
    },

    // Sorting
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },

    // Clear filters
    clearFilters: (state) => {
      state.filterUnitId = '';
      state.filterFromDate = '';
      state.filterToDate = '';
      state.reportData = null;
    },

    // Clear local table filters
    clearTableFilters: (state) => {
      state.filterTableDeviceTypes = [];
      state.filterTableStatuses = [];
      state.filterTableConnectionStatuses = [];
      state.filterLastCommFromDate = '';
      state.filterLastCommToDate = '';
      state.sortColumn = 'deviceAddress';
      state.sortDirection = 'asc';
    },

    // Export to Excel
    exportDeviceReportToExcel: (state) => {
      state.exportLoading = true;
      state.error = null;
    },
    exportDeviceReportToExcelSuccess: (state, action) => {
      state.exportLoading = false;
      state.successMessage = action.payload;
      state.error = null;
    },
    exportDeviceReportToExcelFailure: (state, action) => {
      state.exportLoading = false;
      state.error = action.payload;
    },

    // Export to PDF
    exportDeviceReportToPdf: (state) => {
      state.pdfExportLoading = true;
      state.error = null;
    },
    exportDeviceReportToPdfSuccess: (state, action) => {
      state.pdfExportLoading = false;
      state.successMessage = action.payload;
      state.error = null;
    },
    exportDeviceReportToPdfFailure: (state, action) => {
      state.pdfExportLoading = false;
      state.error = action.payload;
    },

    // Refresh device data
    refreshDeviceData: (state) => {
      state.refreshLoading = true;
      state.error = null;
    },
    refreshDeviceDataSuccess: (state, action) => {
      state.refreshLoading = false;
      state.successMessage = action.payload;
      state.error = null;
    },
    refreshDeviceDataFailure: (state, action) => {
      state.refreshLoading = false;
      state.error = action.payload;
    },

    // Clear messages
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
  },
});

export const {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchDeviceReport,
  fetchDeviceReportSuccess,
  fetchDeviceReportFailure,
  exportDeviceReportToExcel,
  exportDeviceReportToExcelSuccess,
  exportDeviceReportToExcelFailure,
  exportDeviceReportToPdf,
  exportDeviceReportToPdfSuccess,
  exportDeviceReportToPdfFailure,
  refreshDeviceData,
  refreshDeviceDataSuccess,
  refreshDeviceDataFailure,
  setFilterUnitId,
  setFilterFromDate,
  setFilterToDate,
  setFilterTableDeviceTypes,
  setFilterTableStatuses,
  setFilterTableConnectionStatuses,
  setFilterLastCommFromDate,
  setFilterLastCommToDate,
  setSortColumn,
  setSortDirection,
  clearFilters,
  clearTableFilters,
  clearError,
  clearSuccessMessage,
} = deviceReportSlice.actions;

// Selectors
export const selectDeviceReportData = (state) => state.deviceReport?.reportData;
export const selectDeviceReportUnits = (state) => state.deviceReport?.units || [];
export const selectDeviceReportDeviceStatuses = (state) => state.deviceReport?.deviceStatuses || [];
export const selectDeviceReportDeviceSettingTypes = (state) => state.deviceReport?.deviceSettingTypes || [];
export const selectDeviceReportConnectionStatuses = (state) => state.deviceReport?.connectionStatuses || [];
export const selectDeviceReportLoading = (state) => state.deviceReport?.loading || false;
export const selectDeviceReportExportLoading = (state) => state.deviceReport?.exportLoading || false;
export const selectDeviceReportPdfExportLoading = (state) => state.deviceReport?.pdfExportLoading || false;
export const selectDeviceReportRefreshLoading = (state) => state.deviceReport?.refreshLoading || false;
export const selectDeviceReportError = (state) => state.deviceReport?.error;
export const selectDeviceReportSuccessMessage = (state) => state.deviceReport?.successMessage;
export const selectDeviceReportFilterUnitId = (state) => state.deviceReport?.filterUnitId ?? '';
export const selectDeviceReportFilterFromDate = (state) => state.deviceReport?.filterFromDate || '';
export const selectDeviceReportFilterToDate = (state) => state.deviceReport?.filterToDate || '';
// Local Table Filter Selectors
export const selectDeviceReportFilterTableDeviceTypes = (state) => state.deviceReport?.filterTableDeviceTypes || [];
export const selectDeviceReportFilterTableStatuses = (state) => state.deviceReport?.filterTableStatuses || [];
export const selectDeviceReportFilterTableConnectionStatuses = (state) => state.deviceReport?.filterTableConnectionStatuses || [];
export const selectDeviceReportFilterLastCommFromDate = (state) => state.deviceReport?.filterLastCommFromDate || '';
export const selectDeviceReportFilterLastCommToDate = (state) => state.deviceReport?.filterLastCommToDate || '';
// Sorting Selectors
export const selectDeviceReportSortColumn = (state) => state.deviceReport?.sortColumn || '';
export const selectDeviceReportSortDirection = (state) => state.deviceReport?.sortDirection || 'asc';

export default deviceReportSlice.reducer;
